import { useState } from "react";

import { formatCompactWholeNumberWithUnit, formatNumberToShort } from "@/web/lib/formatting";
import type { UnderwritableEntitiesV4 } from "@tycho/web-bindings/types";
import { useTourStep } from "@/web/routes/_protected/smb/-dashboard/-acknowledgments/useTourStep";
import {
  GenericFooterDrawer,
  GenericFooterDrawerTrigger,
  GenericFooterTileLoader,
} from "@/web/routes/_protected/smb/-dashboard/-footer/-GenericFooterDrawer";
import { useSuspendedSmbSuperView } from "@/web/routes/_protected/smb/-dashboard/superView";

export function BudgetTileButton({
  underwritableEntityId,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
}) {
  return (
    <GenericFooterTileLoader>
      <SuspendedBudgetTileButton underwritableEntityId={underwritableEntityId} />
    </GenericFooterTileLoader>
  );
}

function SuspendedBudgetTileButton({
  underwritableEntityId,
}: {
  underwritableEntityId: UnderwritableEntitiesV4["id"];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const tourRef = useTourStep("budget");
  const { currentPurchasingPower, hasFinancialConnections, connections } =
    useSuspendedSmbSuperView(underwritableEntityId);

  const currentPurchasingPowerFormatted = currentPurchasingPower
    ? formatCompactWholeNumberWithUnit(formatNumberToShort(currentPurchasingPower), "Dollars")
    : hasFinancialConnections
      ? "---"
      : "TBD";

  return (
    <>
      <GenericFooterDrawerTrigger
        ref={tourRef}
        title={currentPurchasingPowerFormatted}
        subtitle="Budget"
        color="blue"
        onClick={() => setIsOpen(true)}
      />
      <GenericFooterDrawer title="Budget" isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="flex flex-col gap-4">
          <section className="flex w-full flex-col items-center justify-center gap-4 bg-withus-blue p-10 text-center text-withus-white">
            <span className="withus-strong text-4xl">{currentPurchasingPowerFormatted}</span>
            {hasFinancialConnections && currentPurchasingPower ? (
              <span>
                <p>
                  You can afford to own a{currentPurchasingPowerFormatted.charAt(1) === "8" && "n"}{" "}
                  <b>{currentPurchasingPowerFormatted}</b> building with us.
                </p>
                {/* TODO: add a link to the how we calculate this page */}
              </span>
            ) : hasFinancialConnections ? (
              <span>
                We're crunching the numbers; keep adding financial data to increase the quality of
                our estimate
              </span>
            ) : (
              <span>Add financial data to see your buying power</span>
            )}
          </section>
          <section className="flex flex-col gap-3">
            <span className="text-sm font-semibold">Connections</span>
            {connections.length === 0 ? (
              <p className="text-sm text-muted-foreground">No connections yet.</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {connections.map((conn) => (
                  <li
                    key={`${conn.kind}-${conn.id.toString()}`}
                    className="flex items-center gap-5 border-2 border-withus-blue p-4"
                  >
                    <div className="flex flex-1 items-center gap-2">
                      <span
                        aria-hidden
                        className="size-1.5 shrink-0 rounded-full bg-withus-green"
                      />
                      <span className="flex-1 text-sm font-medium text-withus-blue">
                        {conn.displayName}
                      </span>
                    </div>
                    {/* TODO: per-connection manage flow */}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </GenericFooterDrawer>
    </>
  );
}
